import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Terminal, Download, Trash2, Server, Shield, Clock, Activity,
  Camera, Lock, Unlock, RefreshCw, AlertTriangle, Cpu, Target, Eye, Play,
  ChevronDown, ChevronUp, Image, FileText, Zap, Skull, User,
  ShieldAlert, GitBranch, Move,
} from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n';
import XtermTerminal from '../../components/Terminal/XtermTerminal';
import SmartCommandPanel from '../../components/Terminal/SmartCommandPanel';
import ChainProgressBar from '../../components/Terminal/ChainProgressBar';
import EDRDetectionPanel from '../../components/Terminal/EDRDetectionPanel';

const API_BASE = process.env.REACT_APP_API_URL || '';

const SessionDetails = () => {
  const { t } = useI18n();
  usePageTitle(t('sessions.sessionDetails'));
  const { id } = useParams();
  const navigate = useNavigate();
  const terminalCommandRef = useRef(null);

  const [session, setSession] = useState(null);
  const [commandHistory, setCommandHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [terminalState, setTerminalState] = useState('disconnected');

  // Screenshots
  const [screenshots, setScreenshots] = useState([]);
  const [showScreenshots, setShowScreenshots] = useState(false);

  // Actions loading states
  const [actionLoading, setActionLoading] = useState({
    screenshot: false,
    privesc: false,
    persistence: false,
    close: false,
    detectEdr: false,
    migrate: false,
  });

  // --- Phase 3 state ---
  const [edrResults, setEdrResults] = useState(null);
  const [chainProgress, setChainProgress] = useState({ chainId: null, chainName: null, steps: [], visible: false });
  const [migrationSuggestion, setMigrationSuggestion] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  // Fetch session details
  const fetchSessionDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/exploit/session/${id}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError(t('sessionsExtra.sessionNotFound'));
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSession(data);
      setCommandHistory(data?.commands_executed || []);
      setScreenshots(data?.screenshots || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch session:', err);
      setError(t('sessions.errors.connectionError', 'Erreur de connexion au serveur'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSessionDetails();
    const interval = setInterval(fetchSessionDetails, 5000);
    return () => clearInterval(interval);
  }, [fetchSessionDetails]);

  // Quick command execution via xterm ref
  const executeQuickCommand = (cmd) => {
    if (terminalCommandRef.current) {
      terminalCommandRef.current.sendCommand(cmd);
    }
  };

  // --- Phase 3: Chain execution ---
  const CHAIN_NAMES = {
    full_recon: t('sessionsExtra.chainNames.full_recon'),
    cred_harvest: t('sessionsExtra.chainNames.cred_harvest'),
    pivot_recon: t('sessionsExtra.chainNames.pivot_recon'),
    safe_migrate: t('sessionsExtra.chainNames.safe_migrate'),
  };

  const executeChain = (chainId) => {
    if (!terminalCommandRef.current) return;
    // Initialize progress steps from known chains
    const chainSteps = {
      full_recon: ['System Info', 'Current User', 'Network Config', 'Routing Table', 'Process List', 'ARP Table'],
      cred_harvest: ['Check Privileges', 'Escalade SYSTEM', 'Hash Dump', 'Load Kiwi', 'All Credentials'],
      pivot_recon: ['Interfaces', 'Routes', 'ARP Table', 'Connexions'],
      safe_migrate: ['List Processes', 'Current PID'],
    };
    const steps = (chainSteps[chainId] || []).map((name) => ({ name, status: 'pending' }));
    setChainProgress({ chainId, chainName: CHAIN_NAMES[chainId] || chainId, steps, visible: true });
    terminalCommandRef.current.sendChain(chainId);
  };

  const handleChainStep = useCallback((msg) => {
    setChainProgress((prev) => {
      const steps = prev.steps.map((s) =>
        s.name === msg.step ? { ...s, status: msg.status } : s,
      );
      return { ...prev, steps };
    });
  }, []);

  const handleChainComplete = useCallback(() => {
    // Mark remaining as done, visibility will auto-hide via ChainProgressBar
    setChainProgress((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => (s.status === 'pending' ? { ...s, status: 'done' } : s)),
    }));
  }, []);

  // --- Phase 3: EDR detection ---
  const detectEDR = () => {
    if (!terminalCommandRef.current) return;
    setActionLoading((prev) => ({ ...prev, detectEdr: true }));
    terminalCommandRef.current.sendDetectEDR();
  };

  const handleEDRDetected = useCallback((msg) => {
    setEdrResults(msg);
    setActionLoading((prev) => ({ ...prev, detectEdr: false }));
  }, []);

  // --- Phase 3: Migration ---
  const suggestMigration = () => {
    if (!terminalCommandRef.current) return;
    setActionLoading((prev) => ({ ...prev, migrate: true }));
    terminalCommandRef.current.sendSuggestMigrate();
  };

  const handleMigrationSuggestion = useCallback((msg) => {
    setMigrationSuggestion(msg);
    setActionLoading((prev) => ({ ...prev, migrate: false }));
  }, []);

  const executeMigration = () => {
    if (!migrationSuggestion?.recommendation?.command) return;
    executeQuickCommand(migrationSuggestion.recommendation.command);
    setMigrationSuggestion(null);
  };

  // Take screenshot
  const takeScreenshot = async () => {
    setActionLoading(prev => ({ ...prev, screenshot: true }));
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/session/${id}/screenshot`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error('Screenshot failed');

      const data = await response.json();
      if (data.screenshot) {
        setScreenshots(prev => [...prev, data.screenshot]);
        setShowScreenshots(true);
      }
    } catch (err) {
      console.error('Screenshot failed:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, screenshot: false }));
    }
  };

  // Privilege escalation
  const runPrivesc = async () => {
    if (!window.confirm(t('sessionsExtra.privescConfirm'))) return;

    setActionLoading(prev => ({ ...prev, privesc: true }));
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/session/${id}/privesc`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ method: 'auto' }),
      });

      if (!response.ok) throw new Error('Privilege escalation failed');

      const data = await response.json();
      const privescCmd = {
        id: Date.now(),
        command: '[PRIVESC] Privilege escalation attempt',
        output: data.output || (data.success ? 'Privilege escalation successful!' : 'Privilege escalation failed'),
        timestamp: new Date().toISOString(),
        status: data.success ? 'success' : 'info',
      };
      setCommandHistory(prev => [...prev, privescCmd]);
      fetchSessionDetails();
    } catch (err) {
      console.error('Privesc failed:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, privesc: false }));
    }
  };

  // Install persistence
  const installPersistence = async () => {
    if (!window.confirm(t('sessions.confirmInstallPersistence', 'Install a persistence mechanism?'))) return;

    setActionLoading(prev => ({ ...prev, persistence: true }));
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/session/${id}/persistence`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ method: 'auto' }),
      });

      if (!response.ok) throw new Error('Persistence installation failed');

      const data = await response.json();
      const persistCmd = {
        id: Date.now(),
        command: '[PERSISTENCE] Persistence Installation',
        output: data.output || (data.success ? 'Persistence mechanism installed!' : 'Installation failed'),
        timestamp: new Date().toISOString(),
        status: data.success ? 'success' : 'info',
      };
      setCommandHistory(prev => [...prev, persistCmd]);
      fetchSessionDetails();
    } catch (err) {
      console.error('Persistence failed:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, persistence: false }));
    }
  };

  // Close session
  const handleCloseSession = async () => {
    if (!window.confirm(t('sessions.confirmCloseSession', 'Are you sure you want to close this session?'))) return;

    setActionLoading(prev => ({ ...prev, close: true }));
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/session/${id}/close`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error('Close session failed');
      navigate('/sessions');
    } catch (err) {
      console.error('Failed to close session:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, close: false }));
    }
  };

  // Download session logs
  const downloadLogs = () => {
    const logContent = commandHistory.map(cmd =>
      `[${new Date(cmd.timestamp).toLocaleString()}] $ ${cmd.command}\n${cmd.output}\n`
    ).join('\n');

    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session_${id}_logs.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-brand-green/20 text-brand-green border-brand-green/50',
      inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
      closed: 'bg-red-500/20 text-red-400 border-red-500/50',
      stale: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      idle: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      error: 'bg-red-500/20 text-red-400 border-red-500/50',
    };
    return colors[status] || colors.inactive;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: t('sessions.status.active', 'Active'),
      inactive: t('sessions.status.inactive', 'Inactive'),
      closed: t('sessions.status.closed', 'Closed'),
      stale: t('sessions.status.stale', 'Stale'),
      idle: t('sessions.status.idle', 'Idle'),
      error: t('sessions.status.error', 'Error'),
      unknown: t('sessions.status.unknown', 'Unknown'),
    };
    return labels[status] || t('sessions.status.unknown', 'Unknown');
  };

  const getPrivilegeLevelColor = (level) => {
    const levels = {
      root: 'bg-red-500/20 text-red-400 border-red-500/50',
      admin: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      system: 'bg-red-500/20 text-red-400 border-red-500/50',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      low: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      user: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    };
    return levels[level?.toLowerCase()] || levels.user;
  };

  const getPrivilegeLevelLabel = (level) => {
    const labels = {
      root: 'ROOT',
      admin: 'ADMIN',
      system: 'SYSTEM',
      high: 'HIGH',
      medium: 'MEDIUM',
      low: 'LOW',
      user: 'USER',
    };
    return labels[level?.toLowerCase()] || (level || 'UTILISATEUR').toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('common.notAvailable');
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return t('common.notAvailable');
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-brand-green animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement de la session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-brand-black p-6">
        <div className="max-w-5xl mx-auto text-center py-12">
          <AlertTriangle className="w-20 h-20 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{error || t('sessions.noSessions')}</h2>
          <button onClick={() => navigate('/sessions')} className="btn-green mt-4">
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="page-container">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <button
            onClick={() => navigate('/sessions')}
            className="p-2 hover:bg-brand-black-lighter rounded-lg transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400 hover:text-brand-green" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                <Skull className="w-8 h-8 text-brand-green flex-shrink-0" />
                <span className="truncate">{session.target_ip || session.host?.ip || <span className="text-gray-500 italic">{t('common.notDetectedFeminine')}</span>}</span>
              </h1>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(session.status)}`}>
                  {getStatusLabel(session.status)}
                </span>
                {session.privilege_escalated && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-500/20 text-orange-400 border border-orange-500/50">
                    ROOT
                  </span>
                )}
                {session.persistence_installed && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-400 border border-purple-500/50">
                    PERSISTANT
                  </span>
                )}
              </div>
            </div>
            <p className="text-gray-400">
              Type: {session.session_type || t('sessions.defaultSessionType')} |
              MSF: {session.msf_session_id ? `#${session.msf_session_id}` : 'N/A'} |
              Exploit: {session.exploit_used || t('sessions.manualExploit')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={fetchSessionDetails}
              className="p-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleCloseSession}
              disabled={actionLoading.close}
              className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {actionLoading.close ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {t('sessions.closeSession')}
            </button>
          </div>
        </div>

        {/* Session Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Session Details + Smart Commands */}
          <div className="lg:col-span-1 space-y-4">
            {/* Host Information */}
            <div className="bg-brand-black-light rounded-xl p-5 border border-brand-green/30">
              <div className="flex items-center gap-2 mb-4">
                <Server className="w-5 h-5 text-brand-green" />
                <h2 className="text-lg font-bold text-brand-green">{t('common.info')}</h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex-shrink-0">Adresse IP</span>
                  {session.target_ip || session.host?.ip ? (
                    <span className="text-white font-mono truncate" title={session.target_ip || session.host?.ip}>{session.target_ip || session.host?.ip}</span>
                  ) : (
                    <span className="text-gray-500 italic">{t('common.notDetectedFeminine')}</span>
                  )}
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex-shrink-0">{t('sessions.platform')}</span>
                  {session.platform || session.session_metadata?.platform ? (
                    <span className="text-white truncate" title={session.platform || session.session_metadata?.platform}>{session.platform || session.session_metadata?.platform}</span>
                  ) : (
                    <span className="text-gray-500 italic">{t('common.notDetected')}</span>
                  )}
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex-shrink-0">Architecture</span>
                  <span className="text-white truncate" title={session.arch || session.session_metadata?.arch || 'x64'}>{session.arch || session.session_metadata?.arch || 'x64'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex-shrink-0">Utilisateur</span>
                  {session.user || session.session_metadata?.user ? (
                    <span className="text-white font-mono truncate" title={session.user || session.session_metadata?.user}>{session.user || session.session_metadata?.user}</span>
                  ) : (
                    <span className="text-gray-500 italic">{t('common.notDetected')}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Session Status */}
            <div className="bg-brand-black-light rounded-xl p-5 border border-brand-green/30">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-brand-green" />
                <h2 className="text-lg font-bold text-brand-green">{t('common.status')}</h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500 flex-shrink-0">{t('sessions.privilegeLevel', 'Privilege level')}</span>
                  <span className={`px-2 py-1 rounded text-xs font-bold border flex-shrink-0 ${getPrivilegeLevelColor(session.privilege_level || 'user')}`}>
                    {getPrivilegeLevelLabel(session.privilege_level || 'user')}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex-shrink-0">{t('sessions.startedAt')}</span>
                  <span className="text-white text-xs truncate" title={formatDate(session.opened_at || session.created_at)}>{formatDate(session.opened_at || session.created_at)}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex-shrink-0">{t('sessions.lastActivity', 'Last activity')}</span>
                  <span className="text-white text-xs truncate" title={formatDate(session.last_interaction || session.updated_at)}>{formatDate(session.last_interaction || session.updated_at)}</span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500 flex-shrink-0">Persistence</span>
                  {session.persistence_installed ? (
                    <span className="flex items-center gap-1 flex-shrink-0 text-purple-400">
                      <Lock className="w-3 h-3" />
                      {t('sessions.persistence.installed')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 flex-shrink-0 text-gray-500">
                      <Unlock className="w-3 h-3" />
                      {t('sessions.persistence.notInstalled')}
                    </span>
                  )}
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex-shrink-0">{t('sessions.commands')}</span>
                  <span className="text-white">{commandHistory.length}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex-shrink-0">Screenshots</span>
                  <span className="text-white">{screenshots.length}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-brand-black-light rounded-xl p-5 border border-brand-green/30">
              <h3 className="text-sm font-bold text-brand-green mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                {t('sessions.quickActions')}
              </h3>
              <div className="space-y-2">
                <button
                  onClick={takeScreenshot}
                  disabled={actionLoading.screenshot || session.status !== 'active'}
                  className="w-full py-2 px-3 bg-brand-black border border-gray-700 text-gray-300 rounded-lg hover:border-blue-500/50 hover:text-blue-400 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {actionLoading.screenshot ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </span>
                  {t('sessions.screenshot', 'Screenshot')}
                </button>
                <button
                  onClick={runPrivesc}
                  disabled={actionLoading.privesc || session.privilege_escalated || session.status !== 'active'}
                  className="w-full py-2 px-3 bg-brand-black border border-gray-700 text-gray-300 rounded-lg hover:border-orange-500/50 hover:text-orange-400 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {actionLoading.privesc ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4" />
                    )}
                  </span>
                  {t('sessions.privilegeEscalation', 'Privilege escalation')}
                </button>
                <button
                  onClick={installPersistence}
                  disabled={actionLoading.persistence || session.persistence_installed || session.status !== 'active'}
                  className="w-full py-2 px-3 bg-brand-black border border-gray-700 text-gray-300 rounded-lg hover:border-purple-500/50 hover:text-purple-400 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {actionLoading.persistence ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4" />
                    )}
                  </span>
                  Installer persistance
                </button>
                {/* Detect EDR */}
                <button
                  onClick={detectEDR}
                  disabled={actionLoading.detectEdr || session.status !== 'active'}
                  className="w-full py-2 px-3 bg-brand-black border border-gray-700 text-gray-300 rounded-lg hover:border-red-500/50 hover:text-red-400 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {actionLoading.detectEdr ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <ShieldAlert className="w-4 h-4" />
                    )}
                  </span>
                  Detect EDR
                </button>
                {/* Migration */}
                <button
                  onClick={suggestMigration}
                  disabled={actionLoading.migrate || session.status !== 'active'}
                  className="w-full py-2 px-3 bg-brand-black border border-gray-700 text-gray-300 rounded-lg hover:border-cyan-500/50 hover:text-cyan-400 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {actionLoading.migrate ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Move className="w-4 h-4" />
                    )}
                  </span>
                  Migration Process
                </button>
                <button
                  onClick={downloadLogs}
                  className="w-full py-2 px-3 bg-brand-black border border-gray-700 text-gray-300 rounded-lg hover:border-brand-green/50 hover:text-brand-green transition-colors text-sm text-left flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <Download className="w-4 h-4" />
                  </span>
                  {t('sessions.downloadLogs', 'Download Logs')}
                </button>
              </div>
            </div>

            {/* Smart Commands Panel (replaces old Quick Commands) */}
            <div className="bg-brand-black-light rounded-xl p-5 border border-gray-700">
              <h3 className="text-sm font-bold text-gray-400 mb-3 flex items-center gap-2">
                <Terminal className="w-4 h-4" />
                {t('sessions.quickCommands')}
              </h3>
              <SmartCommandPanel
                sessionType={session.session_type || 'meterpreter'}
                sessionActive={session.status === 'active'}
                onCommand={executeQuickCommand}
                onChain={executeChain}
              />
            </div>
          </div>

          {/* Right Column - Terminal */}
          <div className="lg:col-span-2">
            {/* Migration Suggestion */}
            {migrationSuggestion?.recommendation && (
              <div className="bg-cyan-500/10 rounded-xl border border-cyan-500/30 p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Move className="w-5 h-5 text-cyan-400" />
                    <span className="text-sm font-bold text-cyan-400">
                      {t('sessions.migrationRecommended', 'Recommended migration')}: {migrationSuggestion.recommendation.name} (PID {migrationSuggestion.recommendation.pid})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={executeMigration}
                      className="px-3 py-1 bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 rounded text-xs hover:bg-cyan-500/30 transition-colors"
                    >
                      Migrate
                    </button>
                    <button
                      onClick={() => setMigrationSuggestion(null)}
                      className="px-2 py-1 text-gray-500 hover:text-gray-300 text-xs"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
                {migrationSuggestion.targets?.length > 1 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {migrationSuggestion.targets.slice(1, 6).map((t) => (
                      <span key={t.pid} className="text-xs text-gray-400 bg-gray-800 px-2 py-0.5 rounded">
                        {t.name} ({t.pid})
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* EDR Detection Panel */}
            <EDRDetectionPanel edrResults={edrResults} visible={!!edrResults} />

            {/* Chain Progress Bar */}
            <ChainProgressBar
              chainId={chainProgress.chainId}
              chainName={chainProgress.chainName}
              steps={chainProgress.steps}
              visible={chainProgress.visible}
            />

            {/* Screenshots Panel */}
            {showScreenshots && screenshots.length > 0 && (
              <div className="bg-brand-black-light rounded-xl border border-blue-500/30 mb-4 overflow-hidden">
                <div
                  className="p-4 border-b border-blue-500/20 flex items-center justify-between cursor-pointer"
                  onClick={() => setShowScreenshots(!showScreenshots)}
                >
                  <div className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-blue-400" />
                    <h3 className="font-bold text-blue-400">Screenshots ({screenshots.length})</h3>
                  </div>
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                </div>
                <div className="p-4 grid grid-cols-3 gap-3 max-h-48 overflow-y-auto">
                  {screenshots.map((screenshot, i) => (
                    <div key={i} className="relative group">
                      <img
                        src={`${API_BASE}/api/v1/exploit/screenshots/${screenshot.filename}`}
                        alt={`Screenshot ${i + 1}`}
                        className="w-full h-24 object-cover rounded border border-gray-700"
                        onError={(e) => e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'
                        }
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Eye className="w-6 h-6 text-white" />
                      </div>
                      <div className="absolute bottom-1 left-1 text-xs text-white bg-black/70 px-1 rounded">
                        {new Date(screenshot.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {screenshots.length > 0 && !showScreenshots && (
              <button
                onClick={() => setShowScreenshots(true)}
                className="mb-4 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center gap-2"
              >
                <Image className="w-4 h-4" />
                Voir Screenshots ({screenshots.length})
              </button>
            )}

            {/* Terminal */}
            <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden flex flex-col" style={{ height: '600px' }}>
              {/* Terminal Header */}
              <div className="bg-brand-black border-b border-brand-green/20 p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Terminal className="w-5 h-5 text-brand-green" />
                  <h2 className="text-lg font-bold text-brand-green">{t('sessions.interactiveShell')}</h2>
                </div>
                <div className="flex items-center gap-3">
                  {terminalState === 'connected' ? (
                    <>
                      <div className="w-3 h-3 rounded-full bg-brand-green animate-pulse"></div>
                      <span className="text-xs text-gray-400">{t('sessions.connected', 'Connected')}</span>
                    </>
                  ) : terminalState === 'connecting' ? (
                    <>
                      <div className="w-3 h-3 rounded-full bg-orange-500 animate-pulse"></div>
                      <span className="text-xs text-gray-400">{t('sessions.connecting', 'Connecting...')}</span>
                    </>
                  ) : (
                    <>
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span className="text-xs text-gray-400">{terminalState === 'error' ? t('sessions.status.error', 'Error') : getStatusLabel(session.status)}</span>
                    </>
                  )}
                </div>
              </div>

              {/* xterm.js Terminal */}
              <div className="flex-1 min-h-0">
                <XtermTerminal
                  ref={terminalCommandRef}
                  sessionId={id}
                  onConnectionChange={setTerminalState}
                  onChainStep={handleChainStep}
                  onChainComplete={handleChainComplete}
                  onEDRDetected={handleEDRDetected}
                  onMigrationSuggestion={handleMigrationSuggestion}
                  disabled={session.status !== 'active' && session.status !== 'stale'}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SessionDetails;
